import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL } from "@/mocks/categories";

const SECTIONS = ["s1", "s2", "s3", "s4", "s5", "s6"];
const FAQS = [
  { q: "faq1Q", a: "faq1A" },
  { q: "faq2Q", a: "faq2A" },
  { q: "faq3Q", a: "faq3A" },
  { q: "faq4Q", a: "faq4A" },
  { q: "faq5Q", a: "faq5A" },
];

export default function Article() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || "https://usfanssheet.team";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: t("h1"),
    description: t("shortAnswer"),
    author: {
      "@type": "Organization",
      name: t("siteName"),
    },
    dateModified: "2026-08-20",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/usfans-spreadsheet-guide`,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: t(f.q),
      acceptedAnswer: { "@type": "Answer", text: t(f.a) },
    })),
  };

  const tableRows = [
    { c1: "t1c1", c2: "t1c2", c3: "t1c3" },
    { c1: "t2c1", c2: "t2c2", c3: "t2c3" },
    { c1: "t3c1", c2: "t3c2", c3: "t3c3" },
    { c1: "t4c1", c2: "t4c2", c3: "t4c3" },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Header />

      <main>
        <article className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              {t("breadcrumbHome")}
            </Link>
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm"></i>
            </span>
            <span className="text-foreground-700">{t("breadcrumbCurrent")}</span>
          </nav>

          <h1 className="mt-5 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {t("h1")}
          </h1>

          <p className="mt-4 text-xs text-foreground-500 md:text-sm">
            {t("authorLabel")} · {t("updatedLabel")} {t("updatedValue")}
          </p>

          {/* Short answer */}
          <div className="mt-6 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-700">
              {t("shortAnswerLabel")}
            </p>
            <p className="mt-2 text-base leading-relaxed text-foreground-800">{t("shortAnswer")}</p>
          </div>

          {/* Intro */}
          <p className="mt-6 text-base leading-relaxed text-foreground-700">{t("intro1")}</p>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("intro2")}</p>

          {/* Table of contents */}
          <nav className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground-800">
              {t("tocTitle")}
            </h2>
            <ol className="mt-3 space-y-2">
              {SECTIONS.map((s, i) => (
                <li key={s} className="flex items-start gap-3 text-sm">
                  <span className="font-semibold text-primary-600">{i + 1}.</span>
                  <a
                    href={`#${s}`}
                    className="text-foreground-700 transition-colors hover:text-primary-600"
                  >
                    {t(`toc${i + 1}`)}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Sections */}
          <div className="mt-8 space-y-10">
            {SECTIONS.map((s, i) => (
              <section key={s} id={s}>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {t(`${s}Title`)}
                </h2>
                <p className="mt-3 text-base leading-relaxed text-foreground-700">{t(`${s}Body`)}</p>
              </section>
            ))}
          </div>

          {/* Comparison table */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("tableTitle")}
            </h2>
            <div className="mt-4 overflow-x-auto rounded-lg border border-background-200">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="bg-background-100 text-foreground-800">
                    <th className="px-4 py-3 font-semibold">{t("tableCol1")}</th>
                    <th className="px-4 py-3 font-semibold">{t("tableCol2")}</th>
                    <th className="px-4 py-3 font-semibold">{t("tableCol3")}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.c1} className="border-t border-background-200">
                      <td className="px-4 py-3 font-medium text-foreground-900">{t(row.c1)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(row.c2)}</td>
                      <td className="px-4 py-3 text-foreground-600">{t(row.c3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {t("faqTitle")}
            </h2>
            <div className="mt-4 space-y-3">
              {FAQS.map((f, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={f.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-white"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                    >
                      <span className="text-sm font-semibold text-foreground-900 md:text-base">
                        {t(f.q)}
                      </span>
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      >
                        <i className="ri-arrow-down-s-line text-base"></i>
                      </span>
                    </button>
                    {isOpen && (
                      <p className="border-t border-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                        {t(f.a)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="mt-10 rounded-lg bg-primary-950 p-8 text-center md:p-10">
            <h2 className="font-heading text-xl font-bold text-white md:text-2xl">{t("ctaTitle")}</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-background-300 md:text-base">{t("ctaSub")}</p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="w-full whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 sm:w-auto"
              >
                {t("ctaPrimary")}
              </a>
              <Link
                to="/"
                className="w-full whitespace-nowrap rounded-full border border-white/30 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto"
              >
                {t("ctaSecondary")}
              </Link>
            </div>
          </section>

          {/* Disclaimer */}
          <section className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="font-heading text-sm font-semibold text-foreground-800">
              {t("disclaimerTitle")}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{t("disclaimerBody")}</p>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}